import React, { useState } from "react";
import {
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import MeasureValue, { MeasureChange } from "./MeasureValue";
import ReleaseGateLine from "./ReleaseGateLine";
import { FIRST_VIEW, MEASURES, measureByKey } from "../helpers/turnLevelMeasures";

// The run summary, in order of detail: the release gate line, then the measure
// table (golden set, production, test, change). Whole-run figures: the group
// and script filters do not change it. `production` and `test` are
// { label, batch }; `goldenSet` is the report's golden_set_expected.
const TurnLevelSummary = ({ goldenSet, production, test }) => {
  const [showAll, setShowAll] = useState(false);
  // Match rate and brick rate stay on top; "Show all measures" adds the rest below them.
  const first = FIRST_VIEW.map(measureByKey);
  const measures = showAll ? [...first, ...MEASURES.filter((m) => !FIRST_VIEW.includes(m.key))] : first;

  return (
    <Stack spacing={2}>
      <ReleaseGateLine goldenSet={goldenSet} production={production} test={test} />
      <TableContainer>
        <Table size="small" aria-label="Turn-level measures">
          <TableHead>
            <TableRow>
              <TableCell>Measure</TableCell>
              <TableCell>Golden set</TableCell>
              {production && <TableCell>Production: {production.label}</TableCell>}
              {test && <TableCell>Test: {test.label}</TableCell>}
              {production && test && <TableCell>Change</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {measures.map((m) => (
              <TableRow key={m.key}>
                <TableCell sx={{ maxWidth: 420 }}>
                  <Stack spacing={0.5}>
                    <Tooltip title={`Formula: ${m.formula}`}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        tabIndex={0}
                        sx={{ cursor: "help", width: "fit-content" }}
                      >
                        {m.label}
                      </Typography>
                    </Tooltip>
                    <Typography variant="caption" color="text.secondary">
                      {m.tells}
                    </Typography>
                    {m.note && (
                      <Typography variant="caption" color="warning.main">
                        {m.note}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={0.5}>
                      <Chip label={m.evalType} size="small" variant="outlined" />
                      <Chip label={m.status} size="small" variant="outlined" />
                    </Stack>
                  </Stack>
                </TableCell>
                <TableCell>
                  <MeasureValue measure={m} batch={goldenSet} goldenSet />
                </TableCell>
                {production && (
                  <TableCell>
                    <MeasureValue measure={m} batch={production.batch} />
                  </TableCell>
                )}
                {test && (
                  <TableCell>
                    <MeasureValue measure={m} batch={test.batch} />
                  </TableCell>
                )}
                {production && test && (
                  <TableCell>
                    <MeasureChange measure={m} production={production.batch} test={test.batch} />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack direction="row">
        <Button onClick={() => setShowAll((s) => !s)}>{showAll ? "Show fewer measures" : "Show all measures"}</Button>
      </Stack>
    </Stack>
  );
};

export default TurnLevelSummary;
